import type { MaterialSymbol } from "material-symbols";
import { find, flatten, groupBy, map, prop, sort, uniq, uniqBy } from "ramda";
import { type CSSProperties, useCallback, useEffect, useRef } from "react";
import { Item, type Key, Section, type Selection, useAsyncList } from "react-stately";
import {
  ColoredBullet,
  ColoredLabel,
  DialogComponent,
  MenuOptionItem,
  Modal,
  type MultiSelection,
  MultiSelectMenu,
  SymbolIcon,
} from "widgets";
import { useOverlay } from "widgets/hooks";
import { type GetArrayElementType, sortAlpha } from "widgets/utils";

import { graphql } from "../api/client.ts";
import { PubSubSharedActions } from "../messaging/pubsub-actions.ts";
import { useSubscription } from "../messaging/useSubscription.ts";
import { TagManagement } from "./TagManagement.tsx";

export interface ProjectTagMultiSelectProps {
  label?: string;
  isRequired?: boolean;
  projectId?: number;
  onSelectionChange?: (ids: number[]) => void;
  selectedIds?: number[];
  allowAddingTags?: boolean;
  isDisabled?: boolean;
}

type Tag = GetArrayElementType<Awaited<ReturnType<typeof graphql.getTags>>["itemTags"]>;

interface AddTag {
  id: string;
  title: string;
  icon: MaterialSymbol;
}

export interface List {
  name: string;
  items: Tag[] | AddTag[];
}

const addNewTagsSection = {
  name: "--add-new-tags--",
  items: [{ id: "add-tags", title: "Manage Tags...", icon: "add" }],
} satisfies List;

const idsToKeys = (ids: number[] | undefined): Set<Key> => {
  return new Set(ids?.map((id) => String(id)) ?? []);
};

/**
 * If you don't provide a `projectId`, Tags will be visually merge by name, but the selection will remain a full list of individual tags.
 * So the `initialSelectedKeys` may be too large, but it's not an issue for the rendering.
 */
export const ProjectTagMultiSelect = ({
  selectedIds,
  onSelectionChange,
  allowAddingTags,
  projectId,
  label,
  isRequired,
  isDisabled = false,
}: ProjectTagMultiSelectProps) => {
  const { overlayProps, state } = useOverlay();
  const allTagsRef = useRef<Tag[]>(undefined);
  const allTagsGroupedByNameRef = useRef<Record<string, Tag[]>>(undefined);

  const { items, selectedKeys, isLoading, setSelectedKeys, reload } = useAsyncList<List>({
    initialSelectedKeys: idsToKeys(selectedIds),
    async load() {
      const { itemTags } = await graphql.getTags({ projectId: projectId ?? null });

      allTagsRef.current = itemTags;
      allTagsGroupedByNameRef.current = groupBy((tag) => tag.name, itemTags ?? []) as Record<string, Tag[]>;

      if (!itemTags || itemTags?.length === 0) {
        return { items: allowAddingTags ? [addNewTagsSection] : [] };
      }

      // UniqBy is useful when listing from all projects. In this case, there will never be initialSelectedKeys.
      const list = sort<Tag>((a, b) => sortAlpha(a.name, b.name), uniqBy(prop("name"), itemTags));
      const items = [{ name: "All Tags", items: list }];

      if (allowAddingTags) {
        (items as List[]).push(addNewTagsSection);
      }

      return { items };
    },
  });

  // Because `useAsyncList` rebuilds the helpers we get back on every render
  const setSelectedKeysRef = useRef(setSelectedKeys);
  const reloadRef = useRef(reload);

  const getTagByKey = useCallback(
    (key: Key): Tag =>
      find((tag) => String(key) === String(tag.id), flatten(map((section) => section.items, items))) as Tag,
    [items],
  );

  const updateSelectionChange = useCallback(
    (keys: Selection) => {
      const selectedKeys: number[] = [];
      if (keys === "all") {
        selectedKeys.push(...map((tag) => Number(tag.id), allTagsRef.current ?? []));
      } else {
        for (const key of keys) {
          const tagName = getTagByKey(key)?.name;
          if (tagName && allTagsGroupedByNameRef.current?.[tagName]) {
            const ids = map((tag) => Number(tag.id), allTagsGroupedByNameRef.current[tagName]!);
            selectedKeys.push(...ids);
          }
        }
      }
      onSelectionChange?.(uniq(selectedKeys));
    },
    [getTagByKey, onSelectionChange, allTagsGroupedByNameRef],
  );

  useEffect(() => {
    // Remove all selectedKeys that are not in the items listing.
    let hasChanged = false;

    if (selectedKeys !== "all" && items.length > 0 && !isLoading) {
      for (const key of selectedKeys) {
        if (!getTagByKey(key)) {
          selectedKeys.delete(key);
          hasChanged = true;
        }
      }
    }

    if (hasChanged && selectedKeys !== "all") {
      updateSelectionChange(selectedKeys);
    }
  }, [items, isLoading, selectedKeys, getTagByKey, updateSelectionChange]);

  // Keep the reference of the unstable functions up to date. Not nice but required.
  useEffect(() => {
    setSelectedKeysRef.current = setSelectedKeys;
  }, [setSelectedKeys]);

  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  // controlled component, needed when changes on the selection are triggered from the outside
  useEffect(() => {
    setSelectedKeysRef.current(idsToKeys(selectedIds));
  }, [selectedIds]);

  useEffect(() => {
    reloadRef.current();
  }, [projectId]);

  useSubscription(PubSubSharedActions.UPDATE_TAGS, reload, [projectId]);

  const doRender = useCallback(
    (keys: MultiSelection) => {
      if (keys === "all") {
        return "All tags selected";
      }
      const arr = [...keys];
      const len = arr.length;
      if (len === 1) {
        return getTagByKey(arr[0])?.name;
      }
      return `${len} tags selected`;
    },
    [getTagByKey],
  );

  return (
    <>
      <MultiSelectMenu
        isDisabled={isDisabled || isLoading || (items.length === 0 && !allowAddingTags)}
        label={label}
        isRequired={isRequired}
        items={items}
        rendered={doRender}
        placeholder={isLoading ? "Loading tags" : items.length === 0 ? "No tags found" : "Select tags"}
        icon={
          selectedKeys instanceof Set && selectedKeys.size === 1 ? (
            <ColoredBullet
              color={getTagByKey([...selectedKeys][0])?.color as CSSProperties["backgroundColor"]}
              style={{ margin: "0 5px" }}
            />
          ) : (
            <SymbolIcon
              icon={selectedKeys === "all" || selectedKeys.size > 1 ? "fiber_smart_record" : "fiber_manual_record"}
              filled={selectedKeys === "all" || selectedKeys.size > 0}
            />
          )
        }
        onSelectionChange={(keys) => {
          if (keys instanceof Set) {
            // Always clean "add-tags" from the list
            keys.delete("add-tags");
          }

          setSelectedKeys(keys);
          updateSelectionChange(keys);
        }}
        selectedKeys={selectedKeys}
        onAction={(key) => {
          if (String(key) === "add-tags") {
            state.open();
          }
        }}
      >
        {(section) => {
          if (section.name === addNewTagsSection.name) {
            return (
              <Section key={section.name} items={section.items as AddTag[]}>
                {(item) => (
                  <Item key={String(item.id)} textValue={item.title}>
                    <MenuOptionItem title={item.title} icon={item.icon} />
                  </Item>
                )}
              </Section>
            );
          }

          return (
            <Section key={section.name} items={section.items as Tag[]}>
              {(item) => {
                const nbTags = (allTagsGroupedByNameRef.current?.[item.name] ?? []).length;

                return (
                  <Item key={String(item.id)} textValue={item.name}>
                    <MenuOptionItem
                      title={item.name}
                      icon={
                        nbTags === 0 ? undefined : nbTags === 1 ? (
                          <ColoredLabel
                            color={item.color as CSSProperties["backgroundColor"]}
                            style={{ width: 20, display: "flex", justifyContent: "center" }}
                          />
                        ) : (
                          <SymbolIcon icon="fiber_smart_record" />
                        )
                      }
                      tag={nbTags > 1 ? nbTags : undefined}
                    />
                  </Item>
                );
              }}
            </Section>
          );
        }}
      </MultiSelectMenu>
      {allowAddingTags && (
        <Modal state={state} {...overlayProps}>
          <DialogComponent title="Manage Tags" closeFn={state.close}>
            <TagManagement projectId={projectId} />
          </DialogComponent>
        </Modal>
      )}
    </>
  );
};
